import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { AuthResponse } from '@/features/auth/types'
import { useAuthStore } from '@/stores/auth-store'

const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? 'http://localhost:8000/api/v1'

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30_000,
})

// Attach Bearer token from auth store
apiClient.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState()
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

// Track retried requests to prevent infinite refresh loops
const retriedRequests = new WeakSet<InternalAxiosRequestConfig>()

// Token refresh with request queue
let isRefreshing = false
let failedQueue: Array<{
  resolve: (config: InternalAxiosRequestConfig) => void
  reject: (error: unknown) => void
  config: InternalAxiosRequestConfig
}> = []

function processQueue(error: unknown) {
  for (const pending of failedQueue) {
    if (error) {
      pending.reject(error)
    } else {
      // Re-attach the new token and resolve
      const { accessToken } = useAuthStore.getState()
      if (accessToken) {
        pending.config.headers.Authorization = `Bearer ${accessToken}`
      }
      pending.resolve(pending.config)
    }
  }
  failedQueue = []
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config

    // Only attempt refresh on 401 and if we have a config to retry
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }

    // Don't retry a request that already went through refresh
    if (retriedRequests.has(originalRequest)) {
      useAuthStore.getState().clearAuth()
      return Promise.reject(error)
    }

    // Don't try to refresh the refresh endpoint itself
    if (originalRequest.url?.includes('/auth/refresh')) {
      useAuthStore.getState().clearAuth()
      return Promise.reject(error)
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise<InternalAxiosRequestConfig>((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest })
      }).then((config) => apiClient(config))
    }

    isRefreshing = true
    retriedRequests.add(originalRequest)

    try {
      const { data } = await apiClient.post<AuthResponse>('/auth/refresh')
      useAuthStore.getState().setAuth(data.user, data.access_token)

      // Update the original request with new token
      originalRequest.headers.Authorization = `Bearer ${data.access_token}`

      processQueue(null)
      return apiClient(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError)
      useAuthStore.getState().clearAuth()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)
